import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import propTypes from 'prop-types';

import { useConfig, useDealers, useDeviceType, useMap, useBuyingOptions } from '../../../hooks';

import Filters from './Filters.js';
import Tyre from './Tyre.js';
import GeolocationButton from '../../GeolocationButton';
import DealerList from './DealerList';
import { findClosestDealers } from '../../../lib/utils';
import { SINGLE_DEALER_ROOT } from '../../../lib/data-service';
import MapToggleButton from '../Map/MapToggleButton';
import StageGoogleMapsAutoCompleteForm from '../../StageGoogleMapsAutoCompleteForm';

function renderLocalBusinessSchema(dealers) {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "numberOfItems": dealers.length,
    "itemListElement": dealers.map((dealer, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "LocalBusiness",
        "@id": dealer.uuid,
        "name": dealer.company_name,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": dealer.streetaddress,
          "addressLocality": dealer.city,
          "addressRegion": dealer.state || '',
          "postalCode": dealer.zipcode,
          "addressCountry": dealer.country
        },
        "telephone": dealer.telephone || '',
        "url": window.location.origin + SINGLE_DEALER_ROOT + dealer.slug + '/', //dealer.website_url || '',
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": dealer.lat,
          "longitude": dealer.long
        },
      }
    })),
  });
}

function renderOnlineBusinessSchema(dealers) {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "numberOfItems": dealers.length,
    "itemListElement": dealers.map((dealer, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "OnlineBusiness",
        "@id": dealer.slug,
        "name": dealer.company_name,
        "url": dealer.website_url || '',
      }
    })),
  });
}

function Dealers({
  location, userLocation, selectedFilters, activeDealer,
  onUserLocationChange, onDealerSelect, onFilterChange
}) {
  const { config } = useConfig();
  const { t } = useTranslation('dealers');

  const { isMobile } = useDeviceType();
  const { map, setMapEnabled, mapVisible } = useMap();
  const {
    dealers,
    dealersError,
    dealersLoading,
    dealersLastAction,
    dealersPendingAction,
    locationDealerCount,
    loadMoreDealers,
  } = useDealers({
    location,
    selectedFilters,
  });
  const dealerItemsRef = useRef(null);
  const buyingOptionsProduct = useBuyingOptions();


  //console.log({ dealers, dealersError, dealersLoading, dealersLastAction });

  // Online dealers
  const onlineDealers = useMemo(() => {
    const activeFilters = selectedFilters.map(filterId => config.filters.find(item => item.value === filterId).apiValue).filter(Boolean);
    if (window.ntOnlineDealers.length > 0) {
      return window.ntOnlineDealers.filter(item => activeFilters.length > 0 ? item.company_type.some(type => activeFilters.includes(type)) : true).map(item => {
        item.icon = (window.ntCompanyTypes || []).find(type => type.id === 17);
        return item;
      });
    }

    return [];
  }, [selectedFilters]);

  // Caches last bounds property when view mode changes
  const mapBounds = useRef(null);

  const mapMarkerDealers = useMemo(() => {
    return activeDealer ? [activeDealer] : (dealers || []);
  }, [dealers, activeDealer]);

  const mapViewMode = useMemo(() => {
    let viewMode = 'dealers';

    if (location?.type === 'city') {
      viewMode = 'dealers_city';
    }

    // Mobile only has all dealers mode
    if (activeDealer && !isMobile) {
      if (userLocation) {
        viewMode = 'dealer_directions';
      }
      else {
        viewMode = 'dealer';
      }
    }

    return viewMode;
  }, [activeDealer, location, userLocation, isMobile]);

  function handleFilterChange(filters) {
    onFilterChange(filters);
  }

  const setLocationAndEnableMap = useCallback((location) => {
    onUserLocationChange(location);
    setMapEnabled(true);
  }, [setMapEnabled, onUserLocationChange]);

  const renderedList = useMemo(() => {
    const sorting = {
      type: 'none',
      amount: 0, // Number of special sorted dealers to show first
      radius: 0, // Distance in kilometers to consider special sorted dealers as "close"
    };

    const siteCountry = document.body.getAttribute('data-country');
    if (['US', 'CA', 'AT', 'IT', 'FR', 'CH', 'HU', 'PL', 'CZ'].includes(siteCountry) && location?.type !== 'city') {
      sorting.type = 'nad_first';
      sorting.amount = 3;

      if(['US', 'CA'].includes(siteCountry)) {
        sorting.radius = 24.14; // 15 miles in km
      } else {
        sorting.radius = 15;
      }
    }

    return (
      <DealerList
        dealers={dealers}
        loading={dealersLoading}
        error={dealersError}
        onlineDealers={onlineDealers}
        location={location}
        sorting={sorting}
        onActiveDealerChange={onDealerSelect}
        onUserLocationChange={setLocationAndEnableMap}
      />
    );
  }, [map, dealers, dealersLoading, dealersError, onlineDealers, location, onDealerSelect, setLocationAndEnableMap]);

  const renderedDealerSchemas = useMemo(() => {
    return <>
      {onlineDealers?.length > 0 ?<script type="application/ld+json">{renderOnlineBusinessSchema(onlineDealers)}</script> : null}
      {dealers?.length > 0 ? <script type="application/ld+json">{renderLocalBusinessSchema(dealers)}</script> : null}
    </>
  }, [dealers, onlineDealers]);

  const filters = useMemo(() => {
    return config.filters.map(item => {
      item.active = selectedFilters.includes(item.value);
      return item;
    });
  }, [selectedFilters, config.filters]);

  // Register map listeners
  useEffect(() => {
    if (!map || !mapVisible) {
      return;
    }

    function handleBoundChange(bounds) {
      console.log('Dealer map bounds changed', bounds);
      const loadMoreReasons = ['zoom', 'drag', 'resize'];
      if (loadMoreReasons.includes(bounds.reason)) {
        loadMoreDealers(bounds);
      }
    }

    function handleMarkerClick(dealerUuid) {
      const dealer = dealers.find(item => item.uuid === dealerUuid);
      if (dealer) {
        onDealerSelect(dealer);
      }
    }

    function handleMarkerHover(dealerUuid) {
      if (!dealerItemsRef.current) {
        return;
      }

      const dealerEl = dealerItemsRef.current.querySelector(`.dealer[data-dealer="${dealerUuid}"]`);
      if (dealerEl) {
        dealerEl.classList?.add('hover');
      }

      /*
      // Scroll hovered marker into view
      if (!isMobile && dealerEl) {
        dealerItemsRef.current.scrollTo({ top: dealerEl.offsetTop - dealerItemsRef.current.offsetTop, behavior: 'smooth' });
      }
      */
    }

    function handleMarkerHoverOut(dealerUuid) {
      if (!dealerItemsRef.current) {
        return;
      }
      dealerItemsRef.current.querySelector(`.dealer[data-dealer="${dealerUuid}"]`)?.classList?.remove('hover');
    }

    map.addListener('map_update', handleBoundChange);
    map.addListener('marker_click', handleMarkerClick);
    map.addListener('marker_hover', handleMarkerHover);
    map.addListener('marker_hoverout', handleMarkerHoverOut);

    return () => {
      map.removeListener('map_update', handleBoundChange);
      map.removeListener('marker_click', handleMarkerClick);
      map.removeListener('marker_hover', handleMarkerHover);
      map.removeListener('marker_hoverout', handleMarkerHoverOut);
    };
  }, [map, dealers, selectedFilters, location, config, isMobile, mapVisible]);

  // Update map markers, view mode and/or bounds
  useEffect(() => {
    if (!map) {
      mapBounds.current = null;
      return;
    }

    map.setMarkers(mapMarkerDealers);

    const distance = 100; // kilometers
    const fitBoundsAmount = mapViewMode === 'dealers_city' ? locationDealerCount : 10;

    const oldViewMode = map.getViewMode();
    const viewMode = mapViewMode === 'dealers_city' ? 'dealers' : mapViewMode;
    //console.log('Map Effect', { oldViewMode, viewMode, fitBoundsAmount, dealersLastAction, lastBounds: mapBounds.current });

    if (oldViewMode !== viewMode) {
      const boundsToRestore = ['dealer', 'dealer_directions'].includes(oldViewMode) && mapBounds.current !== null;

      // Store last bounds if dealers view was changed to single dealer view
      if (oldViewMode === 'dealers') {
        mapBounds.current = map.getBounds();
      }

      const viewModeOptions = viewMode === 'dealers' ? {
        fitBoundsDealerUuids: findClosestDealers(mapMarkerDealers, distance, fitBoundsAmount).map(item => item.uuid),
        dealerUuids: boundsToRestore ? [] : mapMarkerDealers.map(item => item.uuid)
      } : {
        dealerUuid: mapMarkerDealers[0].uuid
      };

      map.setViewMode(viewMode, viewModeOptions);
      if (boundsToRestore) {
        map.setBounds(mapBounds.current);
        mapBounds.current = null;
      }
    }
    // Change bounds after location changes
    else if (dealersLastAction === 'location') {
      if (viewMode === 'dealers') {
        map.fitBounds(findClosestDealers(mapMarkerDealers, distance, fitBoundsAmount).map(item => item.uuid), true);
      }
      else if (viewMode === 'dealer') {
        map.fitBounds(mapMarkerDealers, false);
      }
      else {
        map.fitBounds(mapMarkerDealers, true);
      }
    }
  }, [map, locationDealerCount, mapMarkerDealers, mapViewMode, dealersLastAction]);

  // Reset dealer list scroll position when location changes
  useEffect(() => {
    if (dealersLastAction === 'location' && dealersPendingAction === null) {
      dealerItemsRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [dealersLastAction, dealersPendingAction, location, dealers]);

  return (
    <>
      <div className={'sidebar' + (activeDealer ? ' inactive' : '')} inert={activeDealer ? true : false}>
        <div className={'dealer-list'}>
          <div className="dealer-list-header">
            {config.mapTitle && isMobile ? <h1 className="map-title-header">{config.mapTitle}</h1> : null}
            <div className="dealers-title-container">

            {!config.mapTitle ? (
              <h1 className="dealers-title">{t('Find a Nokian Tyres dealer')}</h1>
            ) : (
              <h2 className="dealers-title">{t('Find a Nokian Tyres dealer')}</h2>
            )}
            {buyingOptionsProduct?.selectedCode ? <p className="dealers-title-product">{t('For').toUpperCase()} {buyingOptionsProduct.marketingName.toUpperCase()} ({buyingOptionsProduct.selectedCode})</p> : null}
            </div>
            {isMobile ? (
              <>
                <div className="dealers-action-bar">
                  <StageGoogleMapsAutoCompleteForm key={location?.address} type="inline" initialValue={location?.address} onUserLocationChange={onUserLocationChange} />
                  {isMobile && config.enableMobileMap && (
                    <MapToggleButton enableMap={!!location} iconOnly />
                  )}
                  <Filters filters={filters} onFilterChange={handleFilterChange} iconOnly />
                </div>
                <div className="dealer-map-notices" inert={isMobile && mapVisible ? true : false}>
                  <Tyre />
                </div>
              </>
            ) : (
              <>
                <StageGoogleMapsAutoCompleteForm key={location?.address} type="inline" initialValue={location?.address} onUserLocationChange={onUserLocationChange} />
                <div className="dealer-map-notices" inert={isMobile && mapVisible ? true : false}>
                  <Tyre />
                </div>
                <div className="toggle-and-filters" inert={isMobile && mapVisible ? true : false}>
                  <div className="filter-action-button">
                    {!isMobile && (
                      <GeolocationButton onLocate={onUserLocationChange} text={t('Use my location')} loadingText={t('Loading location')} icon={'Location'} />
                    )}
                    {isMobile && config.enableMobileMap && (
                      <MapToggleButton enableMap={!!location} />
                    )}
                  </div>
                  <Filters filters={filters} onFilterChange={handleFilterChange}/>
                </div>
              </>
            )}
          </div>

          <div ref={dealerItemsRef} className={'dealer-list-items'} inert={isMobile && mapVisible ? true : false}>
            {renderedList}
            {!activeDealer ? renderedDealerSchemas : null}
          </div>
        </div>
      </div>
    </>
  );
}

Dealers.propTypes = {
  activeDealer: propTypes.object,
  location: propTypes.object,
  selectedFilters: propTypes.arrayOf(propTypes.string),
  userLocation: propTypes.object,
  onFilterChange: propTypes.func,
  onDealerSelect: propTypes.func,
  onUserLocationChange: propTypes.func,
};

export default Dealers;
